import { useRef, useState, type JSX } from 'react';
import type { Audience, CommentaryKind } from '@cca/corpus-shared';

export interface ComposerValues {
  kind: CommentaryKind;
  audience: Audience;
  body: string;
  span: string;
  /** Marker position: '' = end of line, else a "li:wi" word key. */
  anchor: string;
  /** The block this note is tethered to (node id). Editable to re-tether. */
  target: string;
  /** ADDITIONAL blocks this same note is also pinned to (multi-anchor). */
  extraTargets: string[];
  /**
   * NEW notes only: if set, this block is linked to an EXISTING note (by id)
   * instead of authoring a fresh one — the existing note simply also appears
   * here. When set, the authoring fields below are irrelevant.
   */
  linkToNoteId: string | null;
}

const KINDS: { v: CommentaryKind; label: string }[] = [
  { v: 'decision', label: 'Decision' },
  { v: 'rationale', label: 'Rationale' },
  { v: 'discussion', label: 'Discussion' },
  { v: 'open', label: 'Open question' },
  { v: 'logic', label: 'Logic' },
  { v: 'purpose', label: 'Purpose' },
  { v: 'flow', label: 'Flow' },
  { v: 'content', label: 'Content' },
  { v: 'sources', label: 'Sources' },
  { v: 'scripture', label: 'Scripture' },
  { v: 'origin', label: 'Origin' },
];
// Audiences are cumulative (commission ⊇ clergy ⊇ public); label by who can read.
const AUDIENCES: { v: Audience; label: string }[] = [
  { v: 'commission', label: 'Commission only' },
  { v: 'clergy', label: 'Clergy & Commission' },
  { v: 'public', label: 'Everyone' },
];

interface NoteComposerProps {
  title: string;
  /** Auto-assigned, rubric-tethered reference for this note (read-only). */
  reference?: string;
  /** Word tokens of the target line(s); lets the author pin the circle to a word. */
  anchorOptions?: { key: string; label: string }[];
  /** Blocks the note may tether to (edit mode); lets the author re-tether. */
  targetOptions?: { id: string; label: string }[];
  /**
   * NEW-note only: existing notes this block may be linked to instead, searchable
   * by reference (e.g. "r1-c1"). Empty/omitted → plain new-note form.
   */
  linkOptions?: { id: string; ref: string; snippet: string }[];
  /** EDIT-note only: this note's current extra anchors, as removable {id,label}. */
  anchorLabels?: { id: string; label: string }[];
  init: ComposerValues;
  /** Hide the anchor-phrase field for thread replies (they inherit the head's). */
  showSpan?: boolean;
  saving: boolean;
  error: string | null;
  onSave: (v: ComposerValues) => void;
  onCancel: () => void;
}

/** Compact form for creating/editing a note — fields only, never markup or ids. */
export function NoteComposer({
  title,
  reference,
  anchorOptions,
  targetOptions,
  linkOptions,
  anchorLabels,
  init,
  showSpan = true,
  saving,
  error,
  onSave,
  onCancel,
}: NoteComposerProps): JSX.Element {
  const [v, setV] = useState<ComposerValues>(init);
  const [linkQuery, setLinkQuery] = useState('');
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Wrap the textarea selection (or insert a placeholder) in a markup delimiter
  // — the note body supports **bold** / *italic* / _underline_ (see note-markup).
  const wrapBody = (mark: string) => {
    const ta = bodyRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = v.body.slice(start, end) || 'text';
    const next = v.body.slice(0, start) + mark + sel + mark + v.body.slice(end);
    setV({ ...v, body: next });
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + mark.length, start + mark.length + sel.length);
    });
  };
  // Linking this block to an existing note → the authoring fields don't apply.
  const linking = v.linkToNoteId !== null;
  const canSave = (linking || v.body.trim().length > 0) && !saving;
  // Re-tethered = the chosen block differs from where the note started; the old
  // word-marker options no longer apply, so the marker resets to end-of-line.
  const retethered = v.target !== init.target;
  const removeAnchor = (id: string) =>
    setV((cur) => ({ ...cur, extraTargets: cur.extraTargets.filter((x) => x !== id) }));

  const linked = linking ? linkOptions?.find((o) => o.id === v.linkToNoteId) : undefined;
  const q = linkQuery.trim().toLowerCase();
  const linkMatches =
    !linking && q.length > 0 && linkOptions
      ? linkOptions.filter((o) => o.ref.toLowerCase().includes(q)).slice(0, 8)
      : [];

  // Only the extra anchors still selected (edit mode), labelled for removal.
  const liveAnchorChips = (anchorLabels ?? []).filter((a) => v.extraTargets.includes(a.id));

  return (
    <div className="composer">
      <div className="composer-title">
        {title}
        {reference && <span className="composer-ref">{reference}</span>}
      </div>

      {/* NEW note: optionally link this block to an existing note by reference. */}
      {linkOptions && linkOptions.length > 0 && (
        <div className="composer-field">
          <span className="composer-label">Link to an existing note (optional)</span>
          {linked ? (
            <div className="composer-linked">
              <span className="composer-linked-ref">{linked.ref}</span>
              <span className="composer-linked-snip">{linked.snippet}</span>
              <button
                type="button"
                className="link-btn"
                onClick={() => setV({ ...v, linkToNoteId: null })}
                disabled={saving}
              >
                ✕ write a new note instead
              </button>
            </div>
          ) : (
            <>
              <input
                className="composer-input"
                value={linkQuery}
                onChange={(e) => setLinkQuery(e.target.value)}
                placeholder="Type a note reference, e.g. r1-c1"
                disabled={saving}
              />
              {linkMatches.length > 0 && (
                <div className="composer-linklist">
                  {linkMatches.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      className="composer-linkopt"
                      onClick={() => {
                        setV({ ...v, linkToNoteId: o.id });
                        setLinkQuery('');
                      }}
                      disabled={saving}
                    >
                      <span className="composer-linked-ref">{o.ref}</span>
                      <span className="composer-linked-snip">{o.snippet}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Authoring fields — hidden when this block is being linked to an existing note. */}
      {!linking && (
        <>
          {targetOptions && targetOptions.length > 0 && (
            <label className="composer-field">
              <span className="composer-label">Tethered to (block)</span>
              <select
                className="composer-select"
                value={v.target}
                onChange={(e) => setV({ ...v, target: e.target.value, anchor: '' })}
                disabled={saving}
              >
                {targetOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* EDIT mode: this note's existing extra anchors, each removable. New
              anchors are added from the block itself (new-note → link by reference). */}
          {liveAnchorChips.length > 0 && (
            <div className="composer-field">
              <span className="composer-label">Also appears at</span>
              <div className="composer-marker">
                {liveAnchorChips.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="marker-chip on"
                    onClick={() => removeAnchor(a.id)}
                    disabled={saving}
                    title="Remove this anchor"
                  >
                    {a.label} ✕
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="composer-row">
            <label className="composer-field">
              <span className="composer-label">Kind</span>
              <select
                className="composer-select"
                value={v.kind}
                onChange={(e) => setV({ ...v, kind: e.target.value as CommentaryKind })}
                disabled={saving}
              >
                {KINDS.map((k) => (
                  <option key={k.v} value={k.v}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="composer-field">
              <span className="composer-label">Visible to</span>
              <select
                className="composer-select"
                value={v.audience}
                onChange={(e) => setV({ ...v, audience: e.target.value as Audience })}
                disabled={saving}
              >
                {AUDIENCES.map((a) => (
                  <option key={a.v} value={a.v}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {showSpan && (
            <label className="composer-field">
              <span className="composer-label">Anchor phrase (optional)</span>
              <input
                className="composer-input"
                value={v.span}
                onChange={(e) => setV({ ...v, span: e.target.value })}
                placeholder="e.g. the word or phrase this note is about"
                disabled={saving}
              />
            </label>
          )}

          {!retethered && anchorOptions && anchorOptions.length > 0 && (
            <div className="composer-field">
              <span className="composer-label">Marker position</span>
              <div className="composer-marker">
                <button
                  type="button"
                  className={`marker-chip${v.anchor === '' ? ' on' : ''}`}
                  onClick={() => setV({ ...v, anchor: '' })}
                  disabled={saving}
                >
                  End of line
                </button>
                {anchorOptions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={`marker-chip${v.anchor === o.key ? ' on' : ''}`}
                    onClick={() => setV({ ...v, anchor: o.key })}
                    disabled={saving}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="composer-field">
            <span className="composer-label">Note</span>
            <div className="note-fmt-bar">
              <button type="button" title="Bold (**text**)" onClick={() => wrapBody('**')}>
                <b>B</b>
              </button>
              <button type="button" title="Italic (*text*)" onClick={() => wrapBody('*')}>
                <i>I</i>
              </button>
              <button type="button" title="Underline (_text_)" onClick={() => wrapBody('_')}>
                <u>U</u>
              </button>
            </div>
            <textarea
              ref={bodyRef}
              className="composer-textarea"
              value={v.body}
              onChange={(e) => setV({ ...v, body: e.target.value })}
              rows={4}
              placeholder="Write the rationale, decision or discussion…"
              disabled={saving}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- deliberate: focused after the user explicitly opens the composer
              autoFocus
            />
          </label>
        </>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="composer-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onSave({ ...v, body: v.body.trim(), span: v.span.trim() })}
          disabled={!canSave}
        >
          {saving ? 'Saving…' : linking ? 'Link here' : 'Save note'}
        </button>
      </div>
    </div>
  );
}
